import React, { useState, useEffect, useRef } from 'react';
import type { Condition, NestedCondition, CountCondition, NotCondition, SimpleCondition } from '../../../../types/policy';
import ConditionEditor from '../../../ConditionEditor';
import { CountConditionEditor } from '../../../CountConditionEditor/CountConditionEditor';
import { isSimpleCondition, isCountCondition, isNestedCondition, isNotCondition } from '../types/policy';

// Define Props for the EditorPanel
interface EditorPanelProps {
  editingNodePath: (string | number)[]; // Path of the node being edited (non-null when panel is shown)
  conditionToEdit: Condition | NestedCondition | NotCondition | CountCondition; // The actual condition data (non-null when panel is shown)
  handleEditorUpdate: (updatedData: Partial<Condition | CountCondition | NestedCondition | NotCondition>) => void; // Callback to update the policy
  handleDeleteNode: (path: (string | number)[]) => void; // Callback to delete the node
  handleToggleGroupType: (path: (string | number)[]) => void; // Callback to toggle group type
  handleToggleNot: (path: (string | number)[]) => void; // Callback to toggle NOT wrapper
  onClose: () => void; // Callback to close the panel
}

export const EditorPanel: React.FC<EditorPanelProps> = ({
  editingNodePath,
  conditionToEdit,
  handleEditorUpdate,
  handleDeleteNode,
  handleToggleGroupType,
  handleToggleNot,
  onClose
}) => {

  // Internal state for panel position, size, dragging, and resizing
  const [panelPosition, setPanelPosition] = useState({ x: 50, y: 50 });
  const [panelSize, setPanelSize] = useState({ width: 600, height: 390 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const resizeStart = useRef({ x: 0, y: 0, width: 0, height: 0 });

  // Effect to handle global mouse move and up events for drag/resize
  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        setPanelPosition({ x: e.clientX - dragStart.current.x, y: e.clientY - dragStart.current.y });
      } else if (isResizing) {
        const dx = e.clientX - resizeStart.current.x;
        const dy = e.clientY - resizeStart.current.y;
        // Enforce minimum dimensions
        setPanelSize({
          width: Math.max(200, resizeStart.current.width + dx),
          height: Math.max(150, resizeStart.current.height + dy) // Adjusted min height
        });
      }
    };
    const onMouseUp = () => {
      if (isDragging) setIsDragging(false);
      if (isResizing) setIsResizing(false);
    };

    // Add listeners only when dragging or resizing
    if (isDragging || isResizing) {
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    }

    // Cleanup function to remove listeners
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [isDragging, isResizing]); // Dependencies ensure effect runs only when drag/resize state changes

  // The actual JSX content, moved from GraphView.tsx
  return (
    <div
        className="editor-panel bg-gray-50 dark:bg-gray-800 shadow-lg rounded-lg border border-gray-200 dark:border-gray-700 p-0 flex flex-col overflow-hidden relative"
        // Position/Size state needs to be handled (next step)
        style={{ position: 'absolute', top: panelPosition.y, left: panelPosition.x, width: panelSize.width, height: panelSize.height }}
    >
        {/* Draggable Header & Conditional Content */}
        {(() => {
            let editorContent = null;
            let editorTitle = "Edit Node";

            // Type guards ensure conditionToEdit properties are accessible
            if (isNotCondition(conditionToEdit)) {
                const notContent = conditionToEdit.not;
                editorTitle = "Edit NOT Group";
                if (isCountCondition(notContent)) {
                    editorTitle = "Edit Count Condition (NOT)";
                    editorContent = <CountConditionEditor condition={notContent} onUpdate={(uc) => handleEditorUpdate({ not: uc })} onRemove={() => handleDeleteNode(editingNodePath)} />;
                } else if (isSimpleCondition(notContent)) {
                    editorTitle = "Edit Condition (NOT)";
                    // Create a wrapper condition for the editor that expects SimpleCondition | NotCondition
                    const wrapperCondition: SimpleCondition = notContent;
                    editorContent = <ConditionEditor 
                        condition={wrapperCondition} 
                        onUpdate={(updatedCondition) => {
                            // Extract SimpleCondition from the result and wrap it in NOT
                            const simpleCondition = 'not' in updatedCondition ? updatedCondition.not : updatedCondition;
                            handleEditorUpdate({ not: simpleCondition as SimpleCondition });
                        }} 
                    />;
                } else if (isNestedCondition(notContent)) {
                    const group = notContent;
                    const groupType = group.allOf ? 'allOf' : 'anyOf';
                    editorTitle = `Edit Group (${groupType} inside NOT)`;
                     editorContent = (
                        <div className="space-y-2">
                            {/* Simplified view for editing nested inside NOT */}
                            <p className="text-sm text-gray-600 dark:text-gray-400">Editing content within a NOT group.</p>
                            <p className="text-xs italic text-gray-500">Delete the outer NOT group to remove negation.</p>
                            <button onClick={() => handleDeleteNode(editingNodePath)} className="w-full px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 text-sm shadow">Delete NOT Group & Content</button>
                        </div>
                    );
                } else { editorContent = <p className="text-red-500">Invalid content inside NOT.</p>; }
            } else if (isCountCondition(conditionToEdit)) {
                editorTitle = "Edit Count Condition";
                editorContent = <CountConditionEditor condition={conditionToEdit} onUpdate={handleEditorUpdate} onRemove={() => handleDeleteNode(editingNodePath)} />;
            } else if (isSimpleCondition(conditionToEdit)) {
                editorTitle = "Edit Condition";
                editorContent = <ConditionEditor condition={conditionToEdit} onUpdate={handleEditorUpdate} />;
            } else if (isNestedCondition(conditionToEdit)) {
                const group = conditionToEdit;
                const groupType = group.allOf ? 'allOf' : 'anyOf';
                editorTitle = `Edit Group (${groupType})`;
                editorContent = (
                    <div className="space-y-2">
                        <button onClick={() => handleToggleGroupType(editingNodePath)} className="w-full px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm">Toggle Type</button>
                        <button onClick={() => handleToggleNot(editingNodePath)} className="w-full px-3 py-1 bg-purple-500 text-white rounded hover:bg-purple-600 text-sm">Toggle NOT</button>
                        <button onClick={() => handleDeleteNode(editingNodePath)} className="w-full px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 text-sm shadow">Delete Group & Children</button>
                    </div>
                );
            } else {
                // Fallback for unexpected condition types
                editorContent = <p className="text-red-500">Cannot edit this node type directly.</p>;
            }

            return (
                <>
                    <div
                        className="flex items-center justify-between bg-gray-100 dark:bg-gray-800 p-2 cursor-move select-none"
                        // Drag start logic needs to be connected (next step)
                        onMouseDown={(e) => {
                            e.stopPropagation();
                            dragStart.current = { x: e.clientX - panelPosition.x, y: e.clientY - panelPosition.y };
                            setIsDragging(true);
                        }}
                    >
                        <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-200">{editorTitle}</h4>
                        <button
                            onClick={onClose} // Use the onClose prop
                            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-100 text-xl"
                            aria-label="Close"
                        >✖</button>
                    </div>
                    {/* Content Area */}
                    <div className="flex-grow p-2 overflow-auto bg-gray-50 dark:bg-gray-800">
                        {editorContent}
                    </div>
                </>
            );
        })()}
        {/* Resize Handle */}
        <div
            className="absolute bottom-1 right-1 w-4 h-4 cursor-se-resize"
            // Resize start logic needs to be connected (next step)
            onMouseDown={(e) => {
                e.stopPropagation();
                setIsResizing(true);
                resizeStart.current = { x: e.clientX, y: e.clientY, width: panelSize.width, height: panelSize.height };
            }}
        />
    </div>
  );
}; 